import * as React from 'react';
import { UserState } from 'app/reducers/state';
import { Callout, Intent, Spinner, H2, Switch, TextArea } from '@blueprintjs/core';
import { executeQueryPutAuth, executeQueryGetAuth } from 'app/utils/api';
import { LargeButton } from '../LargeButton';

interface Props {
    user: UserState;
    classID: string;
    fullWidth?: boolean;
    saOnly?: boolean;
};

interface State {
    waitingOnApi: boolean;
    access_scenario_1: number;
    access_scenario_2: number;
    access_scenario_3: number;
    access_scenario_4: number;
    access_dcoaching: number;
    can_users_see_videos: number;

    client_configuration: string;
}

export class ClassSettingsView extends React.Component<Props, State> {

    state: State = {
        waitingOnApi: true,
        access_scenario_1: 0,
        access_scenario_2: 0,
        access_scenario_3: 0,
        access_scenario_4: 0,
        access_dcoaching: 0,
        can_users_see_videos: 0,
        client_configuration: ''
    }

    componentDidMount() {
        executeQueryGetAuth("class/" + this.props.classID + "/details", {}, this.props.user, (classDetailsResponse) => {
            const classDetails = classDetailsResponse.body;
            
            this.setState({
                waitingOnApi: false,
                access_scenario_1: parseInt(classDetails.access_scenario_1),
                access_scenario_2: parseInt(classDetails.access_scenario_2),
                access_scenario_3: parseInt(classDetails.access_scenario_3),
                access_scenario_4: parseInt(classDetails.access_scenario_4),
                access_dcoaching: parseInt(classDetails.access_dcoaching),
                client_configuration: classDetails.client_configuration,
                can_users_see_videos: parseInt(classDetails.can_users_see_videos)
            })
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.classID !== this.props.classID) {
            executeQueryGetAuth("class/" + this.props.classID + "/details", {}, this.props.user, (classDetailsResponse) => {
                const classDetails = classDetailsResponse.body;
                
                this.setState({
                    waitingOnApi: false,
                    access_scenario_1: parseInt(classDetails.access_scenario_1),
                    access_scenario_2: parseInt(classDetails.access_scenario_2),
                    access_scenario_3: parseInt(classDetails.access_scenario_3),
                    access_scenario_4: parseInt(classDetails.access_scenario_4),
                    access_dcoaching: parseInt(classDetails.access_dcoaching),
                    client_configuration: classDetails.client_configuration,
                    can_users_see_videos: parseInt(classDetails.can_users_see_videos)
                })
            });
        }
    }

    saveSingleSetting(settingName: string, value: number) {
        const {
            access_scenario_1,
            access_scenario_2,
            access_scenario_3,
            access_scenario_4,
            client_configuration,
            access_dcoaching
        } = this.state;

        const {
            classID,
            saOnly
        } = this.props;

        let payload: any = {
            access_scenario_1,
            access_scenario_2,
            access_scenario_3,
            access_scenario_4,
            access_dcoaching
        };

        // Update the specific setting that was changed
        payload[settingName] = value;

        if (saOnly === true) {
            payload['client_configuration'] = client_configuration;

            executeQueryPutAuth("class/access/withc/" + classID, payload, this.props.user, (response) => {
                // Setting saved successfully
            });
        }
        else {
            executeQueryPutAuth("class/access/" + classID, payload, this.props.user, (response) => {
                // Setting saved successfully
            });
        }
    }

    handleUpdateClassSettings = () => {
        this.setState({
            waitingOnApi: true
        });

        const {
            access_scenario_1,
            access_scenario_2,
            access_scenario_3,
            access_scenario_4,
            client_configuration,
            access_dcoaching,
            can_users_see_videos
        } = this.state;

        const {
            classID,
            saOnly
        } = this.props;

        let payload = {
            access_scenario_1,
            access_scenario_2,
            access_scenario_3,
            access_scenario_4,
            access_dcoaching,
            can_users_see_videos: can_users_see_videos ? can_users_see_videos : 1
        }

        if (saOnly === true) {
            payload['client_configuration'] = client_configuration;

            executeQueryPutAuth("class/access/withc/" + classID, payload, this.props.user, (response) => {
                // Just re-load the page to apply it
                this.setState({
                    waitingOnApi: false
                });
            });
        }
        else {
            executeQueryPutAuth("class/access/" + classID, payload, this.props.user, (response) => {
                // Just re-load the page to apply it
                this.setState({
                    waitingOnApi: false
                });
            });
        }
    }

    getWidth = () => {
        if (this.props.fullWidth === true) {
            return '100%';
        }

        return '50%';
    }

    renderSARegion = () => {
        const {
            saOnly
        } = this.props;

        if (saOnly !== true) {
            return undefined;
        }

        return <Callout intent={Intent.WARNING}>
            <b>SA Only Sections</b>

            <p>Set this field to the URL of a JSON object to replace this class's menu with.  Example here: </p>
            <TextArea fill={true} onChange={(e) => {
                this.setState({
                    client_configuration: e.target.value
                })
            }} value={this.state.client_configuration} />
        </Callout>
    }

	render() {
        const {
            waitingOnApi,
            access_scenario_1,
            access_scenario_2,
            access_scenario_3,
            access_scenario_4,
            access_dcoaching,
            can_users_see_videos
        } = this.state;

        if (waitingOnApi) {
            return <Spinner />
        }

        return <span style={{display: 'block', width: this.getWidth(), margin: 'auto'}}>
            {this.renderSARegion()}
            <br />
            <Callout intent={Intent.NONE}>
                <H2>Class Settings</H2>
                <p>Toggle what modules your students should have access to</p>
                <br />

                <Switch label="Access Module 2" checked={access_scenario_1 === 1} onChange={() => {
                    let newAccess = 0;
                    if (access_scenario_1 === 0) { newAccess = 1; }

                    this.setState({
                        access_scenario_1: newAccess
                    }, () => {
                        this.saveSingleSetting('access_scenario_1', newAccess);
                    });
                }} />

                <Switch label="Access Module 3" checked={access_scenario_2 === 1} onChange={() => {
                    let newAccess = 0;
                    if (access_scenario_2 === 0) { newAccess = 1; }

                    this.setState({
                        access_scenario_2: newAccess
                    }, () => {
                        this.saveSingleSetting('access_scenario_2', newAccess);
                    });
                }} />

                <Switch label="Access Module 4" checked={access_scenario_3 === 1} onChange={() => {
                    let newAccess = 0;
                    if (access_scenario_3 === 0) { newAccess = 1; }

                    this.setState({
                        access_scenario_3: newAccess
                    }, () => {
                        this.saveSingleSetting('access_scenario_3', newAccess);
                    });
                }} />

                <Switch label="Access Scenario 5" checked={access_scenario_4 === 1} onChange={() => {
                    let newAccess = 0;
                    if (access_scenario_4 === 0) { newAccess = 1; }

                    this.setState({
                        access_scenario_4: newAccess
                    }, () => {
                        this.saveSingleSetting('access_scenario_4', newAccess);
                    });
                }} />

                <Switch label="Access Dynamic Coaching" checked={access_dcoaching === 1} onChange={() => {
                    let newAccess = 0;
                    if (access_dcoaching === 0) { newAccess = 1; }

                    this.setState({
                        access_dcoaching: newAccess
                    }, () => {
                        this.saveSingleSetting('access_dcoaching', newAccess);
                    });
                }} />

                <Switch label="Can Users See Educator Resources In Client" checked={can_users_see_videos === 1} onChange={() => {
                    let newAccess = 0;
                    if (can_users_see_videos === 0) { newAccess = 1; }

                    this.setState({
                        can_users_see_videos: newAccess
                    });
                }} />



                <LargeButton
                    text="Save Class Settings"
                    intent={Intent.SUCCESS}
                    onClick={() => {
                        this.handleUpdateClassSettings();
                    }}
                />
            </Callout>
        </span>
	}
}
